import React from "react";
import { CheckCircle, Warning, Calendar, Money, Lightning, Clock } from "@phosphor-icons/react";

const STATUS_STYLES = {
  al_dia: { color: "#32D74B", bg: "bg-[#32D74B]/10 border-[#32D74B]/40", icon: CheckCircle, iconWeight: "fill" },
  por_vencer: { color: "#FFD60A", bg: "bg-[#FFD60A]/10 border-[#FFD60A]/40", icon: Clock, iconWeight: "duotone" },
  vencido: { color: "#FF3B30", bg: "bg-[#FF3B30]/10 border-[#FF3B30]/40", icon: Warning, iconWeight: "fill" },
  individual: { color: "#007AFF", bg: "bg-[#007AFF]/10 border-[#007AFF]/40", icon: Lightning, iconWeight: "duotone" },
};

const fmtARS = (v) => new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", minimumFractionDigits: 0 }).format(v || 0);

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("es-AR", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
}

/**
 * Card destacada al tope de la pantalla de Pagos: muestra el estado de
 * cuenta del atleta — plan, próximo vencimiento, totales, status.
 */
export default function AccountStatusCard({ status }) {
  if (!status) return null;
  const style = STATUS_STYLES[status.status] || STATUS_STYLES.al_dia;
  const StatusIcon = style.icon;
  const isIndividual = !status.is_recurring;

  return (
    <div className={`card-base p-5 border ${style.bg}`} data-testid="account-status-card">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <StatusIcon size={28} weight={style.iconWeight} style={{ color: style.color }} />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted">Estado de cuenta</div>
            <div className="font-display font-black uppercase text-2xl tracking-tighter" style={{ color: style.color }} data-testid="account-status-label">
              {status.status_label}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-muted">Plan</div>
          <div className="font-display font-bold uppercase text-sm" data-testid="account-status-plan">{status.plan?.label}</div>
          <div className="text-primary-app font-bold text-lg">{fmtARS(status.plan?.price)}{isIndividual ? " / clase" : " / mes"}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {isIndividual ? (
          <div className="bg-[#0A0A0A] border border-app p-3">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted mb-1">
              <Lightning size={12} weight="bold" /> Modalidad
            </div>
            <div className="text-sm font-semibold">Paga por sesión</div>
            <div className="text-[10px] text-muted mt-1">Sin vencimiento mensual</div>
          </div>
        ) : (
          <div className="bg-[#0A0A0A] border border-app p-3" data-testid="account-next-due">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted mb-1">
              <Calendar size={12} weight="bold" /> Próximo vencimiento
            </div>
            <div className="text-sm font-semibold">{fmtDate(status.next_due_date)}</div>
            <div className="text-[10px] mt-1" style={{ color: style.color }}>
              {status.days_until_due == null
                ? "—"
                : status.days_until_due < 0
                  ? `Vencido hace ${Math.abs(status.days_until_due)} día${Math.abs(status.days_until_due) === 1 ? "" : "s"}`
                  : status.days_until_due === 0
                    ? "Vence hoy"
                    : `Faltan ${status.days_until_due} día${status.days_until_due === 1 ? "" : "s"}`}
            </div>
          </div>
        )}

        <div className="bg-[#0A0A0A] border border-app p-3" data-testid="account-last-payment">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted mb-1">
            <Money size={12} weight="bold" /> Último pago
          </div>
          <div className="text-sm font-semibold">
            {status.last_payment_date ? fmtDate(status.last_payment_date) : "Sin pagos"}
          </div>
          {status.last_payment_amount != null && (
            <div className="text-primary-app text-xs mt-1">{fmtARS(status.last_payment_amount)}</div>
          )}
        </div>

        <div className="bg-[#0A0A0A] border border-app p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Total pagado {new Date().getFullYear()}</div>
          <div className="font-display font-black text-lg text-primary-app">{fmtARS(status.total_paid_ytd)}</div>
          <div className="text-[10px] text-muted mt-1">{status.payment_count} comprobante{status.payment_count === 1 ? "" : "s"}</div>
        </div>

        <div className="bg-[#0A0A0A] border border-app p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted mb-1">
            {isIndividual ? "Histórico" : "Meses cubiertos"}
          </div>
          <div className="font-display font-black text-lg">
            {isIndividual ? fmtARS(status.total_paid_all_time) : (status.covered_months?.length || 0)}
          </div>
          <div className="text-[10px] text-muted mt-1">{isIndividual ? "Total de siempre" : "Este año"}</div>
        </div>
      </div>
    </div>
  );
}
